<template>
  <div class="bsp-backdrop" @click.self="$emit('close')">
    <div class="bsp-modal">
      <div class="bsp-header">
        <span class="bsp-title">Generar estados de cuenta</span>
        <button class="bsp-close" @click="$emit('close')"><span class="mdi mdi-close"></span></button>
      </div>

      <div class="bsp-body">
        <span class="bsp-section-title">Inquilinos incluidos ({{ rows.length }})</span>
        <div class="bsp-tenant-list">
          <div v-for="r in rows" :key="r.contractId" class="bsp-tenant-row">
            <span class="bsp-tenant-name">{{ r.tenantName }}</span>
            <span class="bsp-tenant-amount">{{ formatCurrency(r.pendingCurrent) }}</span>
            <span v-if="r.overdueDays > 0" class="bsp-tenant-overdue">{{ r.overdueDays }}d vencido</span>
          </div>
        </div>

        <span class="bsp-section-title">Contenido a incluir</span>
        <label class="bsp-toggle-row"><input type="checkbox" v-model="options.includePriorPeriods" /> Incluir saldos de periodos anteriores</label>
        <label class="bsp-toggle-row"><input type="checkbox" v-model="options.includeServiceBreakdown" /> Desglose de servicios por concepto</label>
        <label class="bsp-toggle-row"><input type="checkbox" v-model="options.includeTicketReferences" /> Referencia de tickets de mantenimiento</label>
        <label class="bsp-toggle-row"><input type="checkbox" v-model="options.includePaymentHistory" /> Historial de pagos del periodo</label>
        <label class="bsp-toggle-row"><input type="checkbox" v-model="options.includeBankDetails" /> Datos bancarios para pago</label>
        <label class="bsp-toggle-row"><input type="checkbox" v-model="options.includeLatePenaltyClause" /> Cláusula de penalidad por mora</label>
        <label class="bsp-toggle-row"><input type="checkbox" v-model="options.includeLogo" /> Incluir logo del parque</label>

        <span class="bsp-section-title">Formato de entrega</span>
        <label class="bsp-radio-row"><input type="radio" value="DOWNLOAD" v-model="deliveryMode" /> Descargar ZIP con todos los PDFs</label>
        <label class="bsp-radio-row"><input type="radio" value="EMAIL" v-model="deliveryMode" /> Enviar por correo a cada inquilino</label>
        <label class="bsp-radio-row"><input type="radio" value="BOTH" v-model="deliveryMode" /> Ambos</label>
      </div>

      <div class="bsp-footer">
        <div v-if="errorMsg" class="bsp-notice" role="alert">{{ errorMsg }}</div>
        <div v-if="summary" class="bsp-notice bsp-notice--warn" role="status">
          <template v-if="summary.emailedCount">{{ summary.emailedCount === 1 ? 'Se envió 1 estado de cuenta' : `Se enviaron ${summary.emailedCount} estados de cuenta` }} por correo. </template>
          Sin correo de contacto de pagos ({{ summary.missing.length }}): {{ summary.missing.join(', ') }}. Agrégalo en su contrato y vuelve a enviarlos.
        </div>
        <button v-if="summary" class="bsp-generate-btn" @click="$emit('done', summary.result)">Cerrar</button>
        <button v-else class="bsp-generate-btn" :disabled="generating" @click="onGenerate">
          {{ generating ? `Generando ${progressDone} de ${rows.length}...` : `Enviar a todos (${rows.length})` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { API_ORIGIN } from '../../config'
import { ref } from 'vue'
import { generateBulkStatements } from '@/services/charges'

const props = defineProps({
  parkId: { type: String, required: true },
  period: { type: String, required: true },
  rows: { type: Array, required: true },
})

const emit = defineEmits(['close', 'done'])

const options = ref({
  includePriorPeriods: true, includeServiceBreakdown: true, includeTicketReferences: true,
  includePaymentHistory: true, includeBankDetails: true, includeLatePenaltyClause: false, includeLogo: true,
})
const deliveryMode = ref('DOWNLOAD')
const generating = ref(false)
const progressDone = ref(0)
const errorMsg = ref('')
const summary = ref(null)

async function onGenerate() {
  generating.value = true
  errorMsg.value = ''
  try {
    const result = await generateBulkStatements(props.parkId, {
      parkId: props.parkId, contractIds: props.rows.map((r) => r.contractId), period: props.period,
      options: options.value, deliveryMode: deliveryMode.value,
    })
    if (result.zipUrl) window.open(`${API_ORIGIN}${result.zipUrl}`, '_blank')
    const missingIds = result.withoutRecipientIds || []
    if (missingIds.length) {
      // Algunos inquilinos no tenían a quién escribirle: se muestra quiénes, en lugar de cerrar en silencio.
      const names = missingIds.map((id) => props.rows.find((r) => r.contractId === id)?.tenantName || 'Contrato sin nombre')
      summary.value = { emailedCount: result.emailedCount || 0, missing: names, result }
      return
    }
    emit('done', result)
  } catch (err) {
    errorMsg.value = err.message || 'No se pudieron generar los estados de cuenta'
  } finally {
    generating.value = false
  }
}

function formatCurrency(n) { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0) }
</script>

<style scoped>
.bsp-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.4); display: flex; align-items: center; justify-content: center; z-index: 2000; }
.bsp-modal { width: 400px; max-height: 85vh; background: #ffffff; border-radius: 10px; box-shadow: 0 20px 60px rgba(0,0,0,0.3); display: flex; flex-direction: column; overflow: hidden; }
.bsp-header { display: flex; align-items: center; padding: 0.85rem 1.25rem; border-bottom: 1px solid #f1f5f9; }
.bsp-title { font-family: var(--font-display); font-weight: 700; font-size: 15px; color: #1e3a5f; }
.bsp-close { margin-left: auto; background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; }
.bsp-body { flex: 1; min-height: 0; overflow-y: auto; padding: 1rem 1.25rem; display: flex; flex-direction: column; gap: 0.4rem; }
.bsp-body::-webkit-scrollbar { width: 6px; }
.bsp-body::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.bsp-body::-webkit-scrollbar-track { background: transparent; }
.bsp-section-title { font-family: var(--font-body); font-size: 11px; font-weight: 500; text-transform: uppercase; color: #94a3b8; margin-top: 0.6rem; }
.bsp-tenant-list { max-height: 140px; overflow-y: auto; background: #f8f9fc; border-radius: 6px; padding: 0.4rem; }
.bsp-tenant-list::-webkit-scrollbar { width: 6px; }
.bsp-tenant-list::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.bsp-tenant-list::-webkit-scrollbar-track { background: transparent; }
.bsp-tenant-row { display: flex; align-items: center; gap: 0.5rem; height: 26px; font-family: var(--font-body); font-size: 11px; }
.bsp-tenant-name { flex: 1; color: #1e3a5f; }
.bsp-tenant-amount { font-family: var(--font-mono); color: #1e3a5f; }
.bsp-tenant-overdue { color: #dc2626; font-size: 10px; }
.bsp-toggle-row, .bsp-radio-row { display: flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 12px; color: #1e3a5f; }
.bsp-footer { padding: 0.85rem 1.25rem; border-top: 1px solid #f1f5f9; }
.bsp-generate-btn { width: 100%; background: #3b82f6; color: #ffffff; border: none; border-radius: 6px; height: 38px; font-family: var(--font-body); font-size: 13px; font-weight: 500; cursor: pointer; }
.bsp-generate-btn:disabled { opacity: 0.6; }
.bsp-tenant-list {
  max-height: 140px;
  overflow-y: auto;
  background: #f8f9fc;
  border-radius: 6px;
  padding: 0.4rem;
}

.bsp-tenant-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 26px;
  font-family: var(--font-body);
  font-size: 11px;
  min-width: 0;
}

.bsp-tenant-name {
  flex: 1;
  min-width: 0;
  color: #1e3a5f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bsp-tenant-amount {
  font-family: var(--font-mono);
  color: #1e3a5f;
  flex-shrink: 0;
}

.bsp-tenant-overdue {
  color: #dc2626;
  font-size: 10px;
  flex-shrink: 0;
}
.bsp-modal {
  width: 400px;
  max-height: 85vh;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.bsp-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.bsp-tenant-list {
  max-height: 220px;
  overflow-y: auto;
  background: #f8f9fc;
  border-radius: 6px;
  padding: 0.4rem;
  flex-shrink: 0;
}
.bsp-notice { font-family: var(--font-body); font-size: 12px; line-height: 1.4; border-radius: 6px; padding: 0.5rem 0.65rem; margin-bottom: 0.6rem; background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
.bsp-notice--warn { background: #fffbeb; color: #92400e; border-color: #fde68a; }
</style>